/**
 * Iframe messaging utilities for communication with Virtual Hive parent window
 */

export interface VHMessage {
  type: string
  payload?: unknown
  requestId?: string
}

export type VHMessageHandler = (message: VHMessage) => void

export interface VHLocaleMessagePayload {
  /** Sets both UI and CV locale when specific values are not supplied */
  locale?: string
  /** Builder surface language */
  uiLocale?: string
  /** CV document/PDF language */
  cvLocale?: string
  /** Locales the embedding hive wants to expose */
  supportedLocales?: string[]
}

/**
 * Post a message to the parent window (client-mt)
 */
export function postToParent(message: VHMessage, targetOrigin = '*'): void {
  if (window.parent !== window) {
    window.parent.postMessage(message, targetOrigin)
  }
}

/**
 * Subscribe to messages from the parent window
 * @returns Cleanup function
 */
export function onParentMessage(handler: VHMessageHandler, allowedOrigins?: string[]): () => void {
  const listener = (event: MessageEvent) => {
    // Validate origin if specified
    if (allowedOrigins && !allowedOrigins.includes(event.origin)) {
      return
    }

    // Only handle messages that look like VH messages
    if (event.data && typeof event.data.type === 'string') {
      handler(event.data as VHMessage)
    }
  }

  window.addEventListener('message', listener)
  return () => window.removeEventListener('message', listener)
}

/**
 * Send a request to parent and wait for response
 */
export function requestFromParent<T>(type: string, payload?: unknown, timeout = 5000): Promise<T> {
  return new Promise((resolve, reject) => {
    const requestId = crypto.randomUUID()
    const responseType = `${type}_RESPONSE`

    const timer = setTimeout(() => {
      cleanup()
      reject(new Error(`Request timeout: ${type}`))
    }, timeout)

    const cleanup = onParentMessage((message) => {
      if (message.type === responseType && message.requestId === requestId) {
        clearTimeout(timer)
        cleanup()
        resolve(message.payload as T)
      }
    })

    postToParent({ type, payload, requestId })
  })
}

/**
 * Notify parent of events (fire and forget)
 */
export function notifyParent(type: string, payload?: unknown): void {
  postToParent({ type, payload })
}

// Common message types
export const VH_MESSAGES = {
  // Auth
  REQUEST_AUTH_TOKEN: 'REQUEST_AUTH_TOKEN',
  AUTH_TOKEN_RESPONSE: 'REQUEST_AUTH_TOKEN_RESPONSE',
  REQUEST_USER_INFO: 'REQUEST_USER_INFO',
  USER_INFO_RESPONSE: 'REQUEST_USER_INFO_RESPONSE',

  // Navigation
  NAVIGATE: 'NAVIGATE',
  CLOSE: 'CLOSE',

  // Status
  READY: 'CV_BUILDER_READY',
  ERROR: 'CV_BUILDER_ERROR',
  CV_SAVED: 'CV_SAVED',
  CV_EXPORTED: 'CV_EXPORTED',

  // Localization
  /** Payload: { locale?, uiLocale?, cvLocale?, supportedLocales? } */
  SET_LOCALE: 'CV_BUILDER_SET_LOCALE',
  LOCALE_CHANGED: 'CV_BUILDER_LOCALE_CHANGED',
} as const
